import React, { useState, useMemo } from "react";

import { ArrowBack, CalendarMonth, FileDownload, Schedule, School, Search } from "@mui/icons-material";
import { Alert, Box, CircularProgress, IconButton, Tooltip } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import styles from "./BookingsPage.module.scss";
import { apiClient } from "../api/client";
import {
  ServiceBookingHttpResponse,
  ServiceSession,
  SchoolContactServiceResponse,
} from "../api/PartnerPortalGatewayApi";
import { ChildCard, IChildCardData, getChildFullName, getParentFullName } from "../components/common/ChildCard";
import { MainLayout } from "../components/layout/MainLayout";
import { useDownloadCsv, useServicePermissions, convertToCsv } from "../hooks";
import { useAppSelector } from "../store/hooks";
import localISOString from "../utils/localISOString";
import { formatTime } from "../utils/timeFunctions";

dayjs.extend(localISOString);

// Booking status types - Bookings, Attended, and Excluded
type BookingStatus = "bookings" | "attended" | "excluded";

const getStatusLabel = (status: BookingStatus): string => {
  switch (status) {
    case "bookings":
      return "Bookings";
    case "attended":
      return "Attended";
    case "excluded":
      return "Excluded";
  }
};

const formatSessionTime = (start: dayjs.Dayjs, end: dayjs.Dayjs): string => {
  return `${formatTime(start)} - ${formatTime(end)}`;
};

// Convert ServiceBookingHttpResponse to IChildCardData
const toChildCardData = (booking: ServiceBookingHttpResponse): IChildCardData => ({
  childId: booking.childId,
  bookingId: booking.bookingId,
  firstName: booking.childFirstName,
  lastName: booking.childSurname,
  gender: booking.childGender,
  parentFirstName: booking.parentFirstName,
  parentLastName: booking.parentLastName,
  primaryServiceName:
    booking.childPreferredServiceName ?? booking.childPreferredSchoolName ?? booking.primaryServiceName,
});

// Children List Component
interface ChildrenListProps {
  bookings: ServiceBookingHttpResponse[];
}

const ChildrenList: React.FC<ChildrenListProps> = ({ bookings }) => {
  if (bookings.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p className={styles.emptyStateText}>No children in this category</p>
      </div>
    );
  }

  // Debug: Check for duplicate keys
  const keys = bookings.map((booking) => `${booking.childId}-${booking.bookingId}`);
  const duplicates = keys.filter((key, index) => keys.indexOf(key) !== index);
  if (duplicates.length > 0) {
    console.warn("Duplicate booking keys detected:", [...new Set(duplicates)], bookings);
  }

  return (
    <>
      {bookings.map((booking) => (
        <ChildCard
          key={`${booking.childId}-${booking.bookingId}`}
          child={toChildCardData(booking)}
          showStatus={false}
          showGender={false}
        />
      ))}
    </>
  );
};

// Main BookingsPage Component
export const BookingsPage: React.FC = () => {
  const selectedService = useAppSelector((state) => state.service.selectedService);
  const user = useAppSelector((state) => state.auth.user);
  const { hasAllServicesAccess } = useServicePermissions();
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [selectedSession, setSelectedSession] = useState<ServiceSession | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<BookingStatus | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch school contact info to check IsExclusionListRecipient permission
  const { data: schoolContactData } = useQuery({
    queryKey: ["schoolContactServices"],
    queryFn: async (): Promise<SchoolContactServiceResponse[]> => {
      const result = await apiClient.schoolContact.listServicesForContact();
      return result || [];
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000,
  });

  // Check if current user has IsExclusionListRecipient permission for selected service
  // OR if user is an admin with partner_all_services access
  const hasExclusionListRecipientPermission = useMemo(() => {
    // Admin users with partner_all_services can always see excluded children
    if (hasAllServicesAccess()) {
      return true;
    }

    // Non-admin users must have IsExclusionListRecipient set in their school contact
    if (!selectedService?.serviceId || !schoolContactData) return false;
    const currentContact = schoolContactData.find((contact) => contact.serviceId === selectedService.serviceId);
    return currentContact?.isExclusionListRecipient ?? false;
  }, [selectedService?.serviceId, schoolContactData, hasAllServicesAccess()]);

  // Fetch sessions for the selected date
  const {
    data: sessionsData,
    isLoading: sessionsLoading,
    error: sessionsError,
  } = useQuery({
    queryKey: ["serviceSessions", selectedService?.serviceId, selectedDate?.format("YYYY-MM-DD")],
    queryFn: async (): Promise<ServiceSession[]> => {
      if (!selectedService?.serviceId || !selectedDate) {
        return [];
      }

      // Use localISOString plugin to avoid timezone conversion issues
      // This ensures dates are sent as local time strings without UTC shift
      const startOfDay = selectedDate.startOf("day");
      const endOfDay = selectedDate.endOf("day");

      const result = await apiClient.service.getServiceSessionsByDateRange(
        selectedService.serviceId,
        startOfDay,
        endOfDay,
      );
      return result || [];
    },
    enabled: !!(selectedService?.serviceId && selectedDate),
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
  });

  // Fetch bookings for the selected date
  const {
    data: bookingsData,
    isLoading: bookingsLoading,
    isFetching,
    error: bookingsError,
  } = useQuery({
    queryKey: [
      "bookingsByServiceId",
      selectedService?.serviceId,
      selectedDate?.format("YYYY-MM-DD"),
      true, // includeExcluded = true
      true, // excludeFeeWaiverBookings = true
    ],
    queryFn: async (): Promise<ServiceBookingHttpResponse[]> => {
      if (!selectedService?.serviceId || !selectedDate) {
        return [];
      }

      const response = await apiClient.bookings.getBookingsByServiceId(
        selectedService.serviceId!,
        selectedDate,
        true, // includeExcluded = true
        true, // excludeFeeWaiverBookings = true
      );

      return response || [];
    },
    enabled: !!(selectedService?.serviceId && selectedDate),
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
  });

  const isLoading = sessionsLoading || bookingsLoading || isFetching;

  // Group bookings by session
  const sessionBookingsMap = useMemo(() => {
    const map = new Map<number, ServiceBookingHttpResponse[]>();
    (bookingsData || []).forEach((booking) => {
      const rollId = booking.rollId;
      if (!map.has(rollId)) {
        map.set(rollId, []);
      }
      map.get(rollId)!.push(booking);
    });
    return map;
  }, [bookingsData]);

  // Get bookings for selected session
  const selectedSessionBookings = useMemo(() => {
    if (!selectedSession) return [];
    return sessionBookingsMap.get(selectedSession.rollId) || [];
  }, [selectedSession, sessionBookingsMap]);

  // Calculate metrics for selected session
  const totalBookings = selectedSessionBookings.length;
  const attendedCount = selectedSessionBookings.filter((b) => b.attended === true).length;
  const excludedCount = selectedSessionBookings.filter((b) => b.isExcluded === true).length;

  // Check if user has permission to view excluded children
  const hasExcludedListPermission = hasExclusionListRecipientPermission;

  // Get bookings filtered by status and search
  const filteredBookings = useMemo(() => {
    let bookings = selectedSessionBookings;

    // Filter by status
    if (selectedStatus === "attended") {
      bookings = bookings.filter((b) => b.attended === true);
    } else if (selectedStatus === "excluded") {
      bookings = bookings.filter((b) => b.isExcluded === true);
    }
    // "bookings" shows all

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      bookings = bookings.filter((booking) => {
        const childCardData = toChildCardData(booking);
        return (
          getChildFullName(childCardData).toLowerCase().includes(query) ||
          booking.childFirstName?.toLowerCase().includes(query) ||
          booking.childSurname?.toLowerCase().includes(query) ||
          getParentFullName(childCardData).toLowerCase().includes(query)
        );
      });
    }

    return bookings;
  }, [selectedSessionBookings, selectedStatus, searchQuery]);

  // Download CSV hook
  const { isDownloading, downloadCsv } = useDownloadCsv(
    (payload: { selectedSessionBookings: ServiceBookingHttpResponse[] }) => {
      // Transform filtered bookings to CSV format
      const csvData = (payload.selectedSessionBookings || []).map((booking) => ({
        "Session Name": booking.rollName,
        "Session Date": booking.bookingDate ? dayjs(booking.bookingDate).format("YYYY-MM-DD") : "",
        "Child Name": getChildFullName(toChildCardData(booking)),
        "Parent Name": getParentFullName(toChildCardData(booking)),
        "Sign In Time": booking.rollMarkingTimeIn ? booking.rollMarkingTimeIn.format("HH:mm") : "",
        "Sign Out Time": booking.rollMarkingTimeOut ? booking.rollMarkingTimeOut.format("HH:mm") : "",
        Excluded: booking.isExcluded ? "Yes" : "No",
      }));

      return {
        data: convertToCsv(csvData),
      };
    },
  );

  const handleDownloadCsv = async () => {
    if (!selectedService?.serviceId || isDownloading) {
      return;
    }

    try {
      await downloadCsv({
        data: { selectedSessionBookings },
        fileName: `Attendance for ${selectedSession?.rollName} at ${selectedService.serviceName} on ${selectedDate?.format("YYYY-MM-DD")}.csv`,
      });
    } catch (error) {
      console.error("Failed to download CSV:", error);
      // You might want to show an error message to the user here
    }
  };

  const handleDateChange = (newDate: dayjs.Dayjs | null) => {
    if (newDate) {
      setSelectedDate(newDate);
      setSelectedSession(null);
      setSelectedStatus(null);
      setSearchQuery("");
    }
  };

  const handleSessionClick = (session: ServiceSession) => {
    setSelectedSession(session);
    setSelectedStatus(null);
    setSearchQuery("");
  };

  const handleBackToSessions = () => {
    setSelectedSession(null);
    setSelectedStatus(null);
    setSearchQuery("");
  };

  const handleStatusClick = (status: BookingStatus) => {
    setSelectedStatus(status);
    setSearchQuery("");
  };

  const handleBackToStatus = () => {
    setSelectedStatus(null);
    setSearchQuery("");
  };

  // If no service is selected, show appropriate message
  if (!selectedService) {
    return (
      <MainLayout>
        <div className={styles.bookingsPage}>
          <div className={styles.emptyState}>
            <School className={styles.emptyStateIcon} />
            <p className={styles.emptyStateText}>Please select a service to view bookings</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className={styles.bookingsPage}>
        {/* Date Picker Header */}
        <div className={styles.datePickerHeader}>
          <h1 className={styles.datePickerLabel}>Bookings for {selectedDate?.format("dddd, MMMM D, YYYY")}</h1>
          <div className={styles.datePickerWrapper}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Select Date"
                value={selectedDate}
                onChange={handleDateChange}
                format="DD-MMM-YYYY"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </div>
        </div>

        {/* Loading State */}
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : sessionsError || bookingsError ? (
          <Alert severity="error">{sessionsError?.message || bookingsError?.message || "An error occurred"}</Alert>
        ) : sessionsData && sessionsData.length === 0 ? (
          <div className={styles.noSessionsCard}>
            <CalendarMonth className={styles.noSessionsIcon} />
            <p className={styles.noSessionsText}>No sessions found for the selected date</p>
          </div>
        ) : selectedStatus && selectedSession ? (
          // Level 3: Children List
          <>
            <div className={styles.filteredViewHeader}>
              <button className={styles.backButton} onClick={handleBackToStatus}>
                <ArrowBack />
                <span>Back</span>
              </button>
              <h3 className={styles.filteredViewTitle}>
                {selectedSession.rollName} - {getStatusLabel(selectedStatus)} ({filteredBookings.length})
              </h3>
            </div>

            {/* Search Bar */}
            <div className={styles.searchBar}>
              <Search className={styles.searchIcon} />
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search by child or parent name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Children List */}
            <div className={styles.childrenList}>
              <ChildrenList bookings={filteredBookings} />
            </div>
          </>
        ) : selectedSession ? (
          // Level 2: Bookings/Attended Tiles for selected session
          <>
            <div className={styles.filteredViewHeader}>
              <button className={styles.backButton} onClick={handleBackToSessions}>
                <ArrowBack />
                <span>Back</span>
              </button>
              <h3 className={styles.filteredViewTitle}>{selectedSession.rollName}</h3>
              <Tooltip title="Download CSV">
                <IconButton onClick={handleDownloadCsv} color="primary" disabled={isDownloading} sx={{ ml: "auto" }}>
                  {isDownloading ? <CircularProgress size={24} /> : <FileDownload />}
                </IconButton>
              </Tooltip>
            </div>

            <div className={styles.sessionTimeBanner}>
              <Schedule />
              <span>{formatSessionTime(selectedSession.startTime, selectedSession.endTime)}</span>
            </div>

            <div className={styles.metricsGrid}>
              <div
                className={`${styles.metricCard} ${styles.metricCardBookings}`}
                onClick={() => handleStatusClick("bookings")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleStatusClick("bookings")}
              >
                <span className={styles.metricLabel}>Bookings</span>
                <span className={styles.metricValue}>{totalBookings}</span>
              </div>
              <div
                className={`${styles.metricCard} ${styles.metricCardAttended}`}
                onClick={() => handleStatusClick("attended")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleStatusClick("attended")}
              >
                <span className={styles.metricLabel}>Attended</span>
                <span className={styles.metricValue}>{attendedCount}</span>
              </div>
              {excludedCount > 0 && hasExcludedListPermission && (
                <div
                  className={`${styles.metricCard} ${styles.metricCardExcluded}`}
                  onClick={() => handleStatusClick("excluded")}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && handleStatusClick("excluded")}
                >
                  <span className={styles.metricLabel}>Excluded</span>
                  <span className={styles.metricValue}>{excludedCount}</span>
                </div>
              )}
            </div>
          </>
        ) : (
          // Level 1: Session Tiles
          <div className={styles.sessionsGrid}>
            {sessionsData?.map((session) => {
              const sessionBookings = sessionBookingsMap.get(session.rollId) || [];
              return (
                <div
                  key={session.sessionId}
                  className={styles.sessionTile}
                  onClick={() => handleSessionClick(session)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && handleSessionClick(session)}
                >
                  <h3 className={styles.sessionTileName}>{session.rollName || "Session"}</h3>
                  <div className={styles.sessionTileTime}>
                    <Schedule />
                    <span>{formatSessionTime(session.startTime, session.endTime)}</span>
                  </div>
                  <div className={styles.sessionTileCount}>{sessionBookings.length} bookings</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </MainLayout>
  );
};
